import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, CheckCheck, ChevronDown, Search, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useRowNav } from "@/hooks/use-row-nav";
import { KravRow } from "@/components/canvas/KravRow";
import { ItemInspector } from "@/components/canvas/ItemInspector";
import { attachmentCount, groupItems, kravCounts, kravState, matchKrav, normalizeKravFilter, sectionOpenByDefault, workItems, KRAV_FILTERS, KRAV_SEL_KEY } from "@/lib/krav";
import { scrollIntoCanvas } from "@/lib/scroll";

const SECTIONS_KEY = (cid) => `sk.krav.sections.${cid}`;
const readSections = (cid) => { try { return JSON.parse(localStorage.getItem(SECTIONS_KEY(cid)) || "{}"); } catch { return {}; } };

/** «2 · SKROG» – the customer's own SFI section as the primary structure. Same flat head as Kalkyle: a hairline, the chevron far right, no card. */
export function SectionHead({ sfi, label, summary, done, open, onToggle, testId }) {
  return (
    <button type="button" onClick={onToggle} aria-expanded={open} className="group flex w-full items-center gap-4 rounded-[12px] px-3 py-3.5 text-left outline-none transition-colors hover:bg-black/[0.025] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/30" data-testid={testId}>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2.5">
          {sfi != null ? <span className="font-mono text-[14px] text-muted-foreground">{sfi}</span> : null}
          {sfi != null ? <span className="text-muted-foreground/40">·</span> : null}
          <span className="text-[15px] font-semibold text-foreground">{label}</span>
        </div>
        <div className={cn("mt-1 flex items-center gap-1.5 text-[13px]", done ? "text-foreground/70" : "text-muted-foreground")}>{done ? <Check className="h-3.5 w-3.5 text-[hsl(var(--v-ok))]" strokeWidth={2.6} /> : null}{summary}</div>
      </div>
      <ChevronDown className={cn("h-4 w-4 shrink-0 text-muted-foreground/55 transition-transform duration-200", !open && "-rotate-90")} />
    </button>
  );
}

function kravSummary(rows) {
  const c = kravCounts(rows);
  const n = `${c.alle} ${c.alle === 1 ? "arbeidspost" : "arbeidsposter"}`;
  if (!c.gjenstar && !c.oppfolging) return { text: `${c.alle} ${c.alle === 1 ? "post gjennomgått" : "poster gjennomgått"}`, done: true };
  const bits = [n];
  if (c.klar) bits.push(`${c.klar} gjennomgått`);
  if (c.oppfolging) bits.push(`${c.oppfolging} trenger oppfølging`);
  return { text: bits.join(" · "), done: false };
}

/** «Marker som gjennomgått» for every post in view that is simply not looked at yet – two taps, with Angre after. */
function BulkReview({ ids, scoped, projectId, ops }) {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!armed) return undefined; const t = setTimeout(() => setArmed(false), 5000); return () => clearTimeout(t); }, [armed]);
  const n = ids.length;
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/projects/${projectId}/items/gjennomgatt`, { ids });
      ops.apply(r.data);
      const m = r.data.marked || 0;
      const changeId = r.data.context?.undo?.id || null;
      toast.success(m ? `${m} ${m === 1 ? "post" : "poster"} markert som gjennomgått` : "Ingen poster å markere", m && changeId ? { action: { label: "Angre", onClick: () => ops.undo(changeId).catch(() => {}) } } : undefined);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false); setArmed(false);
    }
  };
  if (armed) {
    return (
      <span className="inline-flex items-center gap-1" data-testid="krav-bulk-confirm-wrap">
        <button type="button" onClick={run} disabled={busy} className="inline-flex h-8 items-center gap-1.5 rounded-full bg-[hsl(var(--navy))] px-3 text-[13px] font-semibold text-white transition-colors hover:bg-[hsl(var(--ink-2))] disabled:opacity-60" data-testid="krav-bulk-confirm"><CheckCheck className="h-3.5 w-3.5" /> Bekreft {n} {n === 1 ? "post" : "poster"}</button>
        <button type="button" onClick={() => setArmed(false)} className="h-8 rounded-full px-2.5 text-[13px] text-foreground/65 hover:bg-black/[0.05]" data-testid="krav-bulk-cancel">Avbryt</button>
      </span>
    );
  }
  return <button type="button" onClick={() => setArmed(true)} className="inline-flex h-8 items-center gap-1.5 rounded-full px-2.5 text-[13px] font-medium text-[hsl(var(--navy))] transition-colors hover:bg-[hsl(var(--navy)/0.06)]" title="Poster med åpne avklaringer eller endret grunnlag tas ikke med – de trenger hver sin vurdering" data-testid="krav-bulk-review"><CheckCheck className="h-3.5 w-3.5" /> Marker {scoped ? "" : "alle "}{n} som gjennomgått</button>;
}

/**
 * Arbeidsomfang: section → work item → detail. The list is for scanning; everything about one item lives in the inspector.
 * Sections fold; the ones with work left start open, fully reviewed ones start closed, and Egil's choices are remembered.
 */
export function KravItems({ ctx, project, ops, filter, setFilter, focus, setFocus, setTarget, adding, openRef = null, onOpenRefDone, reportScreen, empty = null }) {
  const all = useMemo(() => ctx.items || [], [ctx.items]);
  const items = useMemo(() => workItems(all), [all]);
  const docs = useMemo(() => ctx.documents || [], [ctx.documents]);
  const [query, setQuery] = useState("");
  const [open, setOpenState] = useState(() => { const id = sessionStorage.getItem(KRAV_SEL_KEY(project.id)); return id && (ctx.items || []).some((i) => i.id === id) ? id : null; });
  const setOpen = (v) => setOpenState((prev) => { const next = typeof v === "function" ? v(prev) : v; if (next) sessionStorage.setItem(KRAV_SEL_KEY(project.id), next); else sessionStorage.removeItem(KRAV_SEL_KEY(project.id)); return next; });
  const [sections, setSections] = useState(() => readSections(project.id));
  const rowRefs = useRef({});
  const searchRef = useRef(null);
  const onRowKey = useRowNav(open, setOpen, { follow: true });
  const counts = useMemo(() => kravCounts(items), [items]);
  // A filter left over from an earlier visit must never hide every post: with nothing in it, show all.
  const f0 = normalizeKravFilter(filter);
  const f = f0 && counts[f0] ? f0 : null;
  useEffect(() => { if (f0 && !counts[f0] && items.length) setFilter(null); }, [f0, counts, items.length, setFilter]);
  const openItem = useMemo(() => all.find((i) => i.id === open) || null, [all, open]);

  useEffect(() => {
    const want = focus || openRef;
    if (!want) return;
    const it = all.find((i) => i.ref === want || i.id === want);
    if (it) {
      setOpen(it.id);
      setQuery("");
      if (it.group && sections[it.group] === false) setSections((s) => ({ ...s, [it.group]: true }));
      requestAnimationFrame(() => scrollIntoCanvas(rowRefs.current[it.id], { block: "center" }));
    }
    if (focus) setFocus(null);
    if (openRef) onOpenRefDone?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, openRef]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open && !openItem) setOpen(null); }, [open, openItem]);
  useEffect(() => { setTarget?.(openItem ? { id: openItem.id, ref: openItem.ref, title: openItem.title } : null); }, [openItem, setTarget]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)) return;
      if (!searchRef.current) return;
      e.preventDefault();
      searchRef.current.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const q = query.trim();
  const rows = useMemo(() => items.filter((i) => (!f || kravState(i).key === f) && matchKrav(i, q)), [items, f, q]);
  const grouped = useMemo(() => groupItems(rows), [rows]);
  const forceOpen = Boolean(f || q);
  const showHeads = grouped.length > 1 || Boolean(grouped[0]?.key);
  const isOpen = (g) => !showHeads || forceOpen || (sections[g.key] ?? sectionOpenByDefault(g.rows));
  const toggleSection = (key, cur) => setSections((s) => { const next = { ...s, [key]: !cur }; localStorage.setItem(SECTIONS_KEY(project.id), JSON.stringify(next)); return next; });
  const doneSections = grouped.filter((g) => !sectionOpenByDefault(g.rows));
  const anyDoneOpen = doneSections.some((g) => isOpen(g));
  const hideDone = () => setSections((s) => { const next = { ...s }; doneSections.forEach((g) => { next[g.key] = false; }); localStorage.setItem(SECTIONS_KEY(project.id), JSON.stringify(next)); return next; });
  const flat = grouped.flatMap((g) => (isOpen(g) ? g.rows : []));
  // Same as Kalkyle: the conversation knows which rows the user is looking at.
  useEffect(() => {
    if (!reportScreen) return;
    reportScreen({ view: "Arbeidsposter", filter: (KRAV_FILTERS.find(([k]) => k === (f || "alle")) || KRAV_FILTERS[0])[1], query: q || undefined, selected: openItem?.ref || undefined, groups: grouped.map((g) => ({ label: g.label, open: isOpen(g), refs: g.rows.slice(0, 60).map((r) => ({ ref: r.ref, label: kravState(r).label || undefined })) })) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportScreen, f, q, openItem?.ref, grouped, sections]);
  const idx = openItem ? flat.findIndex((i) => i.id === openItem.id) : -1;
  const select = (id) => setOpen((v) => (v === id ? null : id));
  const chips = KRAV_FILTERS.filter(([k]) => k === "alle" || counts[k]);
  const unreviewed = rows.filter((i) => kravState(i).key === "gjenstar").map((i) => i.id);
  const allDone = items.length && !counts.gjenstar && !counts.oppfolging;

  if (!items.length) {
    if (empty) return empty;
    return (
      <div className="mt-8 rounded-[14px] border border-dashed border-black/[0.12] px-6 py-14 text-center" data-testid="krav-empty">
        <div className="text-[14px] font-medium">Ingen arbeidsposter ennå</div>
        <p className="mx-auto mt-1 max-w-[440px] text-[13.5px] text-muted-foreground">Slipp kundens forespørsel (ZIP, PDF-spesifikasjon, Excel-svarark eller .msg) i assistenten. Postene får kundens nummerering, originaltekst og kilde – eller legg dem inn med «Ny arbeidspost».</p>
      </div>
    );
  }

  return (
    <section data-testid="krav-items" data-layout={openItem ? "open" : "list"}>
      {/* One compact row that stays while Egil scrolls: search and the view filter – no extra toolbars. */}
      <div className="sticky top-0 z-20 -mx-5 mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-black/[0.06] bg-white/95 px-5 py-2 backdrop-blur-md sm:-mx-8 sm:px-8" data-testid="krav-toolbar">
        <label className="flex h-9 min-w-[220px] flex-1 items-center gap-2 text-[14.5px] sm:max-w-[420px]" data-testid="krav-search">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground/60" />
          <input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => { if (e.key === "Escape") { setQuery(""); e.currentTarget.blur(); } }} placeholder="Søk i arbeidsomfanget …" aria-label="Søk i arbeidsomfanget" className="min-w-0 flex-1 bg-transparent py-1 text-foreground outline-none placeholder:text-muted-foreground/60" data-testid="krav-search-input" />
          {query ? <button type="button" onClick={() => { setQuery(""); searchRef.current?.focus(); }} className="flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:bg-black/[0.06]" aria-label="Tøm søk" data-testid="krav-search-clear"><X className="h-3 w-3" /></button> : null}
        </label>
        {unreviewed.length ? <BulkReview ids={unreviewed} scoped={Boolean(f || q)} projectId={project.id} ops={ops} /> : null}
        <div className="ml-auto flex flex-wrap items-center gap-0.5" role="tablist" aria-label="Vis" data-testid="krav-filter">
          {chips.map(([k, label]) => {
            const active = (f || "alle") === k;
            return <button key={k} type="button" role="tab" aria-selected={active} onClick={() => setFilter(k === "alle" ? null : k)} className={cn("h-8 rounded-[8px] px-2.5 text-[13.5px] transition-colors", active ? "bg-black/[0.06] font-medium text-foreground" : "text-foreground/55 hover:bg-black/[0.035] hover:text-foreground/85")} data-testid={`krav-filter-${k}`}>{label}{k !== "alle" ? <span className="ml-1.5 font-mono text-[12px] tabular-nums opacity-70">{counts[k]}</span> : null}</button>;
          })}
        </div>
      </div>
      {adding ? adding : null}

      <div className="mt-3" role="list" aria-label="Arbeidsomfang" data-testid="krav-list" data-rownav>
        {grouped.map((g) => {
          const openG = isOpen(g);
          const s = kravSummary(g.rows);
          return (
            <div key={g.key} className="scroll-mt-14 border-t border-black/[0.06] first:border-t-0" data-testid={`krav-section-${g.key || "none"}`} data-open={openG ? "1" : "0"}>
              {showHeads ? <SectionHead sfi={g.sfi} label={g.label} summary={s.text} done={s.done} open={openG} onToggle={() => toggleSection(g.key, openG)} testId={`krav-section-toggle-${g.key || "none"}`} /> : null}
              {openG ? (
                <div className={cn(showHeads && "pb-3 pl-9 pr-1")}>
                  {g.rows.map((it) => (
                    <div key={it.id} ref={(el) => { rowRefs.current[it.id] = el; }}>
                      <KravRow item={it} selected={open === it.id} onSelect={() => select(it.id)} onKeyDown={(e) => onRowKey(e, it.id)} attachments={attachmentCount(it, docs)} testId={`krav-row-${it.ref}`} />
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
        {!rows.length ? <p className="py-12 text-center text-[14px] text-muted-foreground" data-testid="krav-no-match">{q ? <>Ingen arbeidsposter matcher «{q}»{f ? " i dette utvalget" : ""}. </> : <>Ingen arbeidsposter i dette utvalget. </>}<button type="button" onClick={() => { setQuery(""); setFilter(null); }} className="font-medium text-[hsl(var(--navy))] hover:underline" data-testid="krav-show-all">{q ? "Tøm søket og vis" : "Vis"} alle {items.length}</button></p> : null}
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-between gap-3 text-[13.5px] text-muted-foreground">
        {allDone ? <span className="inline-flex items-center gap-2 text-foreground/80" data-testid="krav-all-done"><Check className="h-4 w-4 text-[hsl(var(--v-ok))]" strokeWidth={2.6} /> Hele arbeidsomfanget er gjennomgått <button type="button" onClick={() => window.dispatchEvent(new CustomEvent("canvas:goto", { detail: { tab: "kalkyle" } }))} className="font-medium text-[hsl(var(--navy))] underline-offset-[3px] hover:underline" data-testid="krav-go-kalkyle">Gå til kalkyle →</button></span> : <span />}
        {doneSections.length && anyDoneOpen && !forceOpen && showHeads ? <button type="button" onClick={hideDone} className="hover:text-foreground" data-testid="krav-hide-done">Skjul ferdige seksjoner</button> : null}
      </div>

      {openItem ? <ItemInspector item={openItem} ctx={ctx} project={project} ops={ops} onClose={() => setOpen(null)} onPrev={idx > 0 ? () => setOpen(flat[idx - 1].id) : undefined} onNext={idx >= 0 && idx < flat.length - 1 ? () => setOpen(flat[idx + 1].id) : undefined} /> : null}
    </section>
  );
}
